import React from "react";
import { Box, Typography, Container, Link } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { withRouter } from "react-router-dom";

const styles = (theme) => ({
  wrapper: {
    minHeight: "100vh",
    backgroundColor: theme.palette.primary.background,
    padding: theme.spacing(4, 0),
  },
  container: {
    maxWidth: "900px",
    margin: "0 auto",
    padding: theme.spacing(0, 3),
  },
  header: {
    textAlign: "center",
    marginBottom: theme.spacing(4),
  },
  logo: {
    marginBottom: theme.spacing(3),
    textAlign: "center",
  },
  content: {
    "& h2": {
      color: theme.palette.common.text,
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
      fontSize: "1.5rem",
      fontWeight: "600",
    },
    "& h3": {
      color: theme.palette.common.text,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1.5),
      fontSize: "1.2rem",
      fontWeight: "500",
    },
    "& p": {
      color: theme.palette.common.text,
      marginBottom: theme.spacing(2),
      lineHeight: 1.6,
    },
    "& ul": {
      color: theme.palette.common.text,
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(3),
    },
    "& li": {
      marginBottom: theme.spacing(1),
    },
    "& .important": {
      fontWeight: "600",
      color: theme.palette.primary.main,
    },
    "& .warning": {
      backgroundColor: theme.palette.mode === "light" ? "#fff3cd" : "#332701",
      border: `1px solid ${theme.palette.mode === "light" ? "#ffeaa7" : "#664d03"}`,
      borderRadius: "4px",
      padding: theme.spacing(2),
      margin: theme.spacing(2, 0),
    },
  },
  backLink: {
    display: "inline-block",
    marginTop: theme.spacing(4),
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
});

const PrivacyPolicy = ({ classes, theme, history }) => {
  return (
    <Box className={classes.wrapper}>
      <Container className={classes.container}>
        <div className={classes.logo}>
          <Link href="/" style={{ textDecoration: "none" }}>
            <img
              src={
                theme.palette.mode === "light"
                  ? "/images/Auralyze_ai_black_logo.png"
                  : "/images/auralyze_logo_white.png"
              }
              alt="Auralyze Logo"
              style={{ 
                maxWidth: "200px", 
                height: "auto", 
                display: "block",
                margin: "0 auto"
              }}
            />
          </Link>
        </div>

        <div className={classes.header}>
          <Typography 
            variant="h3" 
            fontWeight="bold" 
            sx={{ color: theme.palette.common.text }}
          >
            Privacy Policy
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mt: 2,
              color: theme.palette.common.text,
              opacity: 0.7
            }}
          >
            Last updated: 15th January 2025
          </Typography>
        </div>

        <div className={classes.content}>
          <div className="warning">
            <Typography component="p" className="important">
              IMPORTANT: Your privacy is important to us. This Privacy Policy explains how Auralyze.ai collects, uses, and protects your personal information when you use our AI-powered interview preparation platform.
            </Typography>
          </div>

          <Typography component="h2">1. Information We Collect</Typography>
          
          <Typography component="h3">1.1 Personal Information</Typography>
          <Typography component="p">
            When you create an account or use our services, we collect the following personal information:
          </Typography>
          <ul>
            <li><strong>Account Information:</strong> Full name, email address, password (encrypted), and profile details</li>
            <li><strong>Payment Information:</strong> Billing address, payment method details (processed securely through third-party providers)</li>
            <li><strong>Contact Information:</strong> Phone number (optional), professional details</li>
            <li><strong>Profile Data:</strong> Career goals, interview preferences, and customisation settings</li>
          </ul>

          <Typography component="h3">1.2 Usage Data</Typography>
          <Typography component="p">
            We automatically collect information about how you interact with our platform:
          </Typography>
          <ul>
            <li><strong>Session Data:</strong> Interview practice sessions, completion rates, and performance metrics</li>
            <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system</li>
            <li><strong>Analytics Data:</strong> Feature usage, time spent on platform, click patterns</li>
            <li><strong>Cookies and Tracking:</strong> Essential cookies for functionality and optional analytics cookies</li>
          </ul>

          <Typography component="h3">1.3 Video and Audio Recordings</Typography>
          <Typography component="p">
            <span className="important">Our core service involves recording and analysing your interview practice sessions:</span>
          </Typography>
          <ul>
            <li><strong>Video Recordings:</strong> Practice interview sessions for AI analysis and feedback</li>
            <li><strong>Audio Data:</strong> Speech patterns, tone analysis, and verbal communication assessment</li>
            <li><strong>Metadata:</strong> Recording timestamps, duration, and associated question responses</li>
            <li><strong>AI Analysis Results:</strong> Feedback scores, improvement suggestions, and performance trends</li>
          </ul>

          <Typography component="h2">2. How We Use Your Information</Typography>
          
          <Typography component="h3">2.1 Service Provision</Typography>
          <ul>
            <li>Providing AI-powered interview coaching and feedback</li>
            <li>Personalising your learning experience and recommendations</li>
            <li>Tracking your progress and generating performance analytics</li>
            <li>Enabling account management and authentication</li>
          </ul>

          <Typography component="h3">2.2 Communication</Typography>
          <ul>
            <li>Sending service-related notifications and updates</li>
            <li>Providing customer support and responding to inquiries</li>
            <li>Sharing relevant educational content and tips (with your consent)</li>
            <li>Notifying you about new features and improvements</li>
          </ul>

          <Typography component="h3">2.3 Platform Improvement</Typography>
          <ul>
            <li>Training and improving our AI algorithms (using anonymised data)</li>
            <li>Analysing usage patterns to enhance user experience</li>
            <li>Conducting research and development for new features</li>
            <li>Ensuring platform security and preventing fraud</li>
          </ul>

          <Typography component="h2">3. Data Storage and Security</Typography>
          
          <Typography component="h3">3.1 Data Storage</Typography>
          <Typography component="p">
            Your data is stored securely using industry-standard cloud infrastructure:
          </Typography>
          <ul>
            <li><strong>Primary Storage:</strong> Amazon Web Services (AWS) with UK/EU data centres</li>
            <li><strong>Video Storage:</strong> AWS S3 with AES-256 server-side encryption</li>
            <li><strong>Database Security:</strong> Encrypted databases with regular automated backups</li>
            <li><strong>Access Controls:</strong> Multi-factor authentication and role-based access for our team</li>
          </ul>

          <Typography component="h3">3.2 Security Measures</Typography>
          <ul>
            <li>SSL/TLS encryption for all data transmissions</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Secure coding practices and regular software updates</li>
            <li>Limited access to personal data on a need-to-know basis</li>
            <li>Incident response procedures for potential security breaches</li>
          </ul>

          <Typography component="h2">4. Data Sharing and Disclosure</Typography>
          
          <Typography component="h3">4.1 Third-Party Service Providers</Typography>
          <Typography component="p">
            We may share your data with trusted third-party providers who help us deliver our services:
          </Typography>
          <ul>
            <li><strong>Payment Processors:</strong> Stripe for secure payment processing</li>
            <li><strong>Cloud Infrastructure:</strong> AWS for hosting and data storage</li>
            <li><strong>Analytics Services:</strong> Google Analytics for usage insights (anonymised)</li>
            <li><strong>Email Services:</strong> Mailchimp for service communications</li>
            <li><strong>Customer Support:</strong> Zendesk for support ticket management</li>
          </ul>

          <Typography component="h3">4.2 Legal Requirements</Typography>
          <Typography component="p">
            We may disclose your information if required by law or to:
          </Typography>
          <ul>
            <li>Comply with legal obligations or court orders</li>
            <li>Protect our rights, property, or safety</li>
            <li>Protect the rights, property, or safety of our users</li>
            <li>Prevent fraud or illegal activities</li>
          </ul>

          <Typography component="h3">4.3 Business Transfers</Typography>
          <Typography component="p">
            In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity, with the same privacy protections.
          </Typography>

          <Typography component="h2">5. Data Retention</Typography>
          
          <Typography component="p">
            We retain your personal data only as long as necessary:
          </Typography>
          <ul>
            <li><strong>Active Accounts:</strong> Data retained while your account is active and for legitimate business purposes</li>
            <li><strong>Inactive Accounts:</strong> Data deleted after 24 months of inactivity (with 30-day advance notice)</li>
            <li><strong>Video Recordings:</strong> Retained for up to 3 years or until account deletion, whichever comes first</li>
            <li><strong>Payment Data:</strong> Retained for 7 years for tax and accounting purposes</li>
            <li><strong>Legal Requirements:</strong> Some data may be retained longer to comply with legal obligations</li>
            <li><strong>Anonymised Data:</strong> May be retained indefinitely for research and service improvement</li>
          </ul>

          <Typography component="h2">6. Your Rights</Typography>
          
          <Typography component="p">
            Under UK GDPR and data protection laws, you have the following rights:
          </Typography>
          
          <Typography component="h3">6.1 Access and Portability</Typography>
          <ul>
            <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
            <li><strong>Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
          </ul>

          <Typography component="h3">6.2 Correction and Deletion</Typography>
          <ul>
            <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete personal data</li>
            <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
          </ul>

          <Typography component="h3">6.3 Processing Control</Typography>
          <ul>
            <li><strong>Right to Restrict Processing:</strong> Limit how we use your data in certain circumstances</li>
            <li><strong>Right to Object:</strong> Object to processing for direct marketing or legitimate interests</li>
            <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for data processing at any time</li>
          </ul>

          <Typography component="h3">6.4 Exercising Your Rights</Typography>
          <Typography component="p">
            To exercise any of these rights, contact us at <strong>privacy@auralyze.ai</strong> or through your account settings. We will respond within 30 days of receiving your request.
          </Typography>

          <Typography component="h2">7. Cookies and Tracking Technologies</Typography>
          
          <Typography component="h3">7.1 Essential Cookies</Typography>
          <Typography component="p">
            We use essential cookies that are necessary for the platform to function:
          </Typography>
          <ul>
            <li>Authentication and session management</li>
            <li>Security and fraud prevention</li>
            <li>Load balancing and performance optimisation</li>
            <li>User preference storage</li>
          </ul>

          <Typography component="h3">7.2 Analytics Cookies</Typography>
          <Typography component="p">
            With your consent, we use analytics cookies to understand how users interact with our platform and improve our services. You can opt out of these through your browser settings or our cookie preferences.
          </Typography>

          <Typography component="h2">8. International Data Transfers</Typography>
          
          <Typography component="p">
            While we primarily store data within the UK/EU, some of our service providers may be located outside the European Economic Area (EEA). We ensure adequate protection through:
          </Typography>
          <ul>
            <li>Standard Contractual Clauses (SCCs) with data processors</li>
            <li>Adequacy decisions where applicable</li>
            <li>Additional safeguards for data protection</li>
          </ul>

          <Typography component="h2">9. Children's Privacy</Typography>
          
          <Typography component="p">
            Our service is not intended for individuals under 16 years of age. We do not knowingly collect personal information from children under 16. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
          </Typography>

          <Typography component="h2">10. Updates to This Policy</Typography>
          
          <Typography component="p">
            We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will:
          </Typography>
          <ul>
            <li>Notify you of significant changes via email or platform notification</li>
            <li>Update the "Last updated" date at the top of this policy</li>
            <li>Give you the opportunity to review changes before they take effect</li>
          </ul>

          <Typography component="h2">11. Contact Information</Typography>
          
          <Typography component="p">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </Typography>
          <ul>
            <li><strong>Email:</strong> privacy@auralyze.ai</li>
            <li><strong>General Support:</strong> support@auralyze.ai</li>
            <li><strong>Phone:</strong> +44 75954 70805</li>
            <li><strong>Address:</strong> Auralyze Limited, [Company Address], United Kingdom</li>
          </ul>

          <Typography component="p">
            For complaints about data processing, you can also contact the Information Commissioner's Office (ICO) at <Link href="https://ico.org.uk">ico.org.uk</Link>.
          </Typography>

          <Typography component="h2">12. Definitions</Typography>
          
          <ul>
            <li><strong>"Personal Data"</strong> means any information relating to an identified or identifiable natural person</li>
            <li><strong>"Processing"</strong> means any operation performed on personal data, including collection, storage, and use</li>
            <li><strong>"Data Controller"</strong> means Auralyze Limited, who determines the purposes and means of processing</li>
            <li><strong>"Data Subject"</strong> means the individual to whom the personal data relates (you)</li>
            <li><strong>"GDPR"</strong> means the General Data Protection Regulation (EU) 2016/679 and UK GDPR</li>
          </ul>
        </div>

        <Link href="/" className={classes.backLink}>
          ← Back to Home
        </Link>
      </Container>
    </Box>
  );
};

export default withStyles(styles, { withTheme: true })(withRouter(PrivacyPolicy)); 